import microApp from '@micro-zoe/micro-app'

const _window = window as any

export type IAppItem = {
  id: string
  code: string
  name: string
  baseRoute: string
  url: string
  destroy: number
  mainApp: number
  order: number
  status: number
  memo: string
}

export type INavItem = {
  id: string
  appId: string
  appCode: string
  appBaseRoute: string
  pid: string
  idPath: string
  mainApp: number
  code: string
  name: string
  icon: string
  type: number
  path: string
  order: number
  status: number
  memo: string
  permissions?: Array<IPermissionItem>
  children?: Array<INavItem>
}

export type IPermissionItem = {
  id: string
  appId: string
  navId: string
  code: string
  name: string
  memo?: string
}

export type IPermission = {
  appId: string
  navId: string
  navCode: string
  permissions: Array<string>
}

/**
 * 校验路由权限
 * @param routes
 * @param navs
 * @returns
 */
export const checkFunctionCode = (routes: Array<any>): Array<any> => {
  let globalData = null
  if (_window.__MICRO_APP_BASE_APPLICATION__) {
    // 主应用获取全局数据
    globalData = microApp.getGlobalData()
  } else {
    // 子应用获取全局数据
    globalData = _window.microApp.getGlobalData()
  }

  const { org } = globalData || {}
  const { navs = [] } = org || {}
  
  // 校验code
  const checkCode = (routes: Array<any>) => {
    return routes.filter((route: any) => {
      if (Array.isArray(route.children) && route.children.length > 0) {
        const _children: any = checkCode(route.children)
        if (_children.length > 0) {
          route.children = _children
          return true
        }
        return false
      }

      // 根据code匹配有权限路由
      const { handle = {} } = route

      // 公共路由不校验，isPublic 优先级高于code
      if (handle.isPublic) {
        return true
      }

      // 判断code是否有权限
      if (handle.code) {
        return navs.findIndex((n: any) => n.code === handle.code) >= 0
      }

      return false
    })
  }

  return checkCode(routes)
}

/**
 * 生成导航树
 * @param navs
 * @param parentId
 */
export const genNavTree = (navs: Array<INavItem>, parentId: string = '', type: number = -1): Array<any> => {
  const children = [] as Array<INavItem>

  navs
    .filter((n: INavItem) => {
      if (type < 0) {
        return n.pid === parentId
      }
      return n.pid === parentId && n.type === type
    })
    .forEach((n: INavItem) => {
      children.push({
        ...n,
        id: n.id,
        pid: parent ? parentId : n.id,
        idPath: n.idPath,
        children: genNavTree(navs, n.id, type)
      })
    })

  return children
}

/**
 * 根据路径获取树形节点
 * @param navTree
 * @param idPath
 * @returns
 */
export const getNavTreeByIds = (navTree: Array<INavItem>, idPath: Array<string>) => {
  const idArray = [...idPath]
  let dataSoure = navTree

  const filterNode = (ids: Array<string>): INavItem | null => {
    const id = ids.shift()
    const node = dataSoure.find((n) => n.id === id)
    if (!node) {
      return null
    }

    if (ids.length === 0) {
      return node
    }

    dataSoure = node.children || []

    return filterNode(ids)
  }

  return filterNode(idArray)
}

/**
 * 获取导航中第一个path路径
 * @param parentId
 */
export const getNavFirstPath = (parentId?: string) => {
  let globalData = null
  if (_window.__MICRO_APP_BASE_APPLICATION__) {
    // 主应用获取全局数据
    globalData = microApp.getGlobalData()
  } else {
    // 子应用获取全局数据
    globalData = _window.microApp.getGlobalData()
  }

  const { org } = globalData || {}
  const { apps = [], navs = [] } = org || {}

  const rootNavs = navs.filter((n: INavItem) => {
    if (parentId) {
      return n.pid === parentId
    } else {
      return !n.pid
    }
  })

  // 获取第一个path
  const filterNav = (ns: Array<INavItem>): INavItem | null => {
    for (let i = 0; i < ns.length; i++) {
      const n = ns[i]
      const nc = navs.filter((nsi: any) => nsi.parentId === n.id)
      if (nc.length > 0) {
        return filterNav(nc)
      } else {
        if (n.path) {
          return n
        }
      }
    }

    return null
  }

  const nav = filterNav(rootNavs)
  if (!nav) return

  console.log('nav:navnav:', nav)

  const app = apps.find((a: any) => a.id === nav.appId)
  if (!app) return

  if (!app.mainApp) {
    return (app.baseRoute + nav.path).replace('//', '/')
  } else {
    return nav.path
  }
}

/**
 * 获取导航中第一个path非空的导航
 * @param navs
 * @param parentId
 * @returns
 */
export const getFirstNavPath = (navs: Array<INavItem>, parentId?: string): INavItem | null => {
  // 导航列表
  let _navs = []

  // 当前菜单中的一级菜单
  let _children = []

  if (parentId) {
    _navs = navs.filter((n: INavItem) => n.idPath.startsWith(parentId))
    _children = _navs.filter((n: INavItem) => n.pid === parentId)
  } else {
    _navs = navs
    _children = navs
  }

  _navs = _navs.sort((a, b) => a.order - b.order)
  _children = _children.sort((a, b) => a.order - b.order)

  // 获取第一个path
  const filterNav = (ns: Array<INavItem>): INavItem | null => {
    for (let i = 0; i < ns.length; i++) {
      const n = ns[i]

      const nc = _navs.filter((nsi) => nsi.pid === n.id)

      if (nc.length > 0) {
        return filterNav(nc)
      } else {
        if (n.path) {
          return n
        }
      }
    }

    return null
  }

  return filterNav(_children)
}

/**
 * 获取当前的路由path
 * @returns
 */
export const getCurrentRoutePath = () => {
  let path = '/'
  const _window = window as any

  const { pathname, search } = window.location

  // 微前端环境下
  if (_window.__MICRO_APP_ENVIRONMENT__) {
    path = pathname.replace(_window.__MICRO_APP_BASE_ROUTE__ || '/', '')
  } else {
    // 非微前端环境直接渲染
    path = pathname
  }

  path = path + search

  return path.startsWith('/') ? path : '/' + path
}

/**
 * 根据匹配的路由，定位导航
 * @param navs
 * @param matches
 */
export const matchNavs = (navs: Array<INavItem>, matches: Array<any>) => {
  if (!matches || !Array.isArray(matches) || matches.length <= 0) {
    return []
  }

  const { handle = {} } = matches[matches.length - 1] || {}
  if (!handle || !handle.code) {
    return []
  }

  const { code = '' } = handle
  // 获取匹配到的导航，并获取路径
  const nav = navs.find((n: INavItem) => n.code === code)
  if (!nav) return []

  let navIds = []
  if (nav.idPath) {
    navIds = nav.idPath.split(',')
    navIds.push(nav.id)
  } else {
    navIds = [nav.id]
  }

  const _matchNav: any = []
  navIds.forEach((n: string) => {
    const _nav = navs.find((item: INavItem) => item.id === n)
    _matchNav.push(_nav)
  })

  return _matchNav
}
